import React from 'react';
import PropTypes from 'prop-types';
import DayPicker, { DateUtils } from 'react-day-picker';
import { getUuid } from '@front10/helpers/dist/uuid';
import dayjs from 'dayjs';
import getStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import DatePickerDay from '../DatePicker/DatePickerDay';
import Box from '../../elements/Box';
import Icon from '../../elements/Icon';
import Button from '../../elements/Button';
import Text from '../../elements/Text';
import WeekDays from './atoms/WeekDays';
import Footer from './atoms/Footer';
import Caption from './atoms/Caption';
import Input from './atoms/Input';
import { isNilOrEmpty } from '../../../helpers/utils';
import { isDayBefore, isDayInRange, isFutureOrPresentDay, isDayAfter } from '../../../helpers/date';
import customParseFormat from 'dayjs/plugin/customParseFormat';

//Finit State machine used to model behavior of date selection
const STATES = {
  EMPTY: 'EMPTY',
  START: 'START',
  BOTH: 'BOTH',
  INPSTART: 'INPSTART',
  INPEND: 'INPEND'
};
// Making inmutable the states
Object.freeze(STATES);

const machine = {
  [STATES.EMPTY]: {
    selectFrom: STATES.START,
    selectTo: STATES.BOTH,
    toEnd: STATES.INPEND,
    toStart: STATES.INPSTART
  },
  [STATES.START]: {
    clear: STATES.EMPTY,
    selectFrom: STATES.START,
    selectTo: STATES.BOTH,
    toEnd: STATES.INPEND,
    toStart: STATES.INPSTART
  },
  [STATES.BOTH]: {
    clear: STATES.EMPTY,
    selectFrom: STATES.START,
    selectTo: STATES.BOTH,
    toEnd: STATES.INPEND,
    toStart: STATES.INPSTART
  },
  [STATES.INPSTART]: {
    clear: STATES.EMPTY,
    selectFrom: STATES.START,
    toEnd: STATES.INPEND,
    toStart: STATES.INPSTART
  },
  [STATES.INPEND]: {
    clear: STATES.EMPTY,
    selectTo: STATES.BOTH,
    toEnd: STATES.INPEND,
    toStart: STATES.INPSTART
  }
};
// Making inmutable the machine variable
Object.freeze(machine);

const NOT_AVALIABLES_DAYS = -1;
const KEY_ENTER = 13;
const DATE_OUT_OF_RANGE = 0;
const INCORRECT_FORMAT = 1;

class DatesRangeSelectorMobile extends React.Component {
  state = {
    from: this.props.defaultValue.start,
    to: this.props.defaultValue.end,
    showCloseIcon: !!(this.props.defaultValue.start || this.props.defaultValue.end),
    validfrom: undefined,
    validto: undefined,
    numberOfMonths: this.props.numberOfMonths,
    machineState: STATES.EMPTY,
    lastMachineState: undefined,
    statesHistory: [],
    initialFocus: '',
    isCheckedFlexibleDates: this.props.enableFlexibleDates && this.props.checkedFlexibleDates,
    flexibleDatesValues: this.props.flexibleDatesRangeValues.defaultValue,
    availableDays: this.props.availableDays || NOT_AVALIABLES_DAYS,
    typeError: undefined
  };

  handleFlexibleDatesChange = (value) => {
    this.setState({
      flexibleDatesValues: value
    });
  };

  handleIsCheckedFlexibleDatesChange = (value) => {
    this.setState({
      isCheckedFlexibleDates: value
    });
  };

  modifiers = {
    today: (day) => DateUtils.isSameDay(day, dayjs().toDate()),
    beginRange: (day) => {
      return DateUtils.isSameDay(day, this.state.from);
    },
    endRange: (day) => DateUtils.isSameDay(day, this.state.to),
    selected: (day) =>
      DateUtils.isDayInRange(day, {
        from: this.state.from,
        to: this.state.to
      }),
    past: (day) => {
      if (this.props.departure) {
        return (
          isDayBefore(day, dayjs().add(Number(this.props.departure.start), 'day').toDate()) ||
          isDayAfter(
            day,
            dayjs()
              .add(Number(this.props.departure.start) + Number(this.props.departure.end) - 1, 'day')
              .toDate()
          )
        );
      } else {
        return DateUtils.isDayBefore(day, dayjs().toDate());
      }
    },
    disabled: {
      before:
        this.props.departure && Number(this.props.departure.start)
          ? dayjs().add(Number(this.props.departure.start), 'day').toDate()
          : this.currentDate,
      after:
        this.props.departure &&
        Number(this.props.departure.end) >= 0 &&
        Number(this.props.departure.start) >= 0
          ? dayjs()
              .add(Number(this.props.departure.end) + Number(this.props.departure.start) - 1, 'day')
              .toDate()
          : dayjs()
              .add(this.props.availableDays - 1, 'day')
              .toDate()
    }
  };

  id = getUuid();

  currentDate = dayjs().toDate();

  constructor(props) {
    super(props);
    props.focusReferences(this);
    this.validStartDateRange = 0;
    this.validEndDateRange = 1;
    this.validStarEndDateRange = 2;
    this.invalidStarEndDateRange = 3;
  }

  componentDidMount = () => {
    this.addScrollEvent();
  };

  componentWillUnmount = () => {
    this.removeScrollEvent();
  };

  componentDidUpdate = (previousProps, previousState) => {
    const { showPicker } = this.props;
    if (showPicker) {
      this.addScrollEvent();

      if (
        previousState.initialFocus === '' &&
        this.state.initialFocus === previousState.initialFocus
      ) {
        if (this.toFocusReference.reference === document.activeElement) {
          this.setState({ initialFocus: 'to', machineState: STATES.INPEND });
        } else if (this.fromFocusReference.reference === document.activeElement) {
          this.setState({ initialFocus: 'from', machineState: STATES.INPSTART });
        }
      }
    } else {
      this.removeScrollEvent();
      if (
        previousState.initialFocus !== '' &&
        previousState.initialFocus === this.state.initialFocus
      ) {
        this.setState({ initialFocus: '' });
      }
    }
  };

  addScrollEvent = () => {
    if (this.container) {
      this.elementScrollable = this.container.querySelector('.DayPicker');
      if (this.elementScrollable) {
        this.elementScrollable.addEventListener('scroll', this.onScroll);
      }
    }
  };

  removeScrollEvent = () => {
    if (this.elementScrollable) {
      this.elementScrollable.removeEventListener('scroll', this.onScroll);
    }
  };

  onScroll = ($event) => {
    const { maxMonthsToShow } = this.props;
    const { numberOfMonths } = this.state;
    if (
      (!maxMonthsToShow || maxMonthsToShow >= numberOfMonths + 3) &&
      parseInt($event.target.scrollTop + $event.target.getBoundingClientRect().height) ===
        parseInt($event.target.scrollHeight)
    ) {
      this.setState({ numberOfMonths: numberOfMonths + 3 }, () => {
        if (maxMonthsToShow && maxMonthsToShow <= numberOfMonths + 3) {
          this.removeScrollEvent();
        }
      });
    }
  };

  onChange = (date, name) => {
    this.setState({
      [name]: date,
      [`valid${name}`]: undefined,
      showCloseIcon: true,
      typeError: undefined
    });
  };

  update = () => {
    const { onChange } = this.props;
    const { from, to, isCheckedFlexibleDates, flexibleDatesValues } = this.state;
    onChange({
      start: from,
      end: to,
      flexibleDates: isCheckedFlexibleDates ? flexibleDatesValues : 0,
      availableDays: this.props.availableDays || NOT_AVALIABLES_DAYS,
      enabledFlexibleDates: this.props.enableFlexibleDates,
      checkedFlexibleDates: this.state.isCheckedFlexibleDates
    });
  };

  send = (transition, payload = {}) => {
    const { machineState, statesHistory } = this.state;
    const currentState = machine[machineState];
    const nextState = currentState[transition];
    if (nextState) {
      this._dispatch(transition, payload);
      const newStatesHistory = [...statesHistory, machineState];
      // console.log('machine state', machineState, ' =>', transition, ' =>', nextState); //For debug
      this.setState({
        machineState: nextState,
        statesHistory: newStatesHistory,
        lastMachineState: machineState
      });
    } else {
      console.error('machine state: failed transition ', transition);
    }
  };
  _dispatch = (transition, payload) => {
    switch (transition) {
      case 'clear':
        break;
      case 'selectTo':
        this.selectTo(payload.date, payload.anyDate);
        break;
      case 'selectFrom':
        this.selectFrom(payload.date, payload.anyDate);
        break;
      case 'toStart':
      case 'toEnd':
        break;
      default:
    }
  };

  containerReference = (el) => {
    this.container = el;
  };

  renderDay = (day) => {
    const { dayItems, renderDay } = this.props;
    const { modifiers } = this;
    let { modifiersColors } = this.props;
    modifiersColors = modifiersColors || {};
    if (renderDay && typeof renderDay === 'function') {
      return renderDay(day);
    }
    let props = Object.assign({}, dayItems);

    Object.keys(modifiersColors).every((i) => {
      if (modifiers[i] && typeof modifiers[i] === 'function' && modifiers[i](day)) {
        props = Object.assign(props, modifiersColors[i]);
        return false;
      }
      return true;
    });
    return <DatePickerDay border={false} day={day} {...props} />;
  };

  renderNavbarElement = () => {
    return null;
  };

  captionElement = ({ date }) => {
    const { captionItem } = this.props;
    return <Caption {...captionItem} date={date} />;
  };

  onInvalid = (name) => {
    this.setState({ [`valid${name}`]: false });
  };

  selectFrom = (date, anyDate = false) => {
    const { to } = this.state;
    if (!to || dayjs(date).isBefore(dayjs(to))) {
      this.onChange(date, 'from');
      this.fromFocusReference.updateValue(date);
    } else if (anyDate) {
      this.onChange(date, 'from');
      this.fromFocusReference.updateValue(date);

      if (dayjs(date).isAfter(dayjs(to))) {
        this.setState({ to: undefined }, () => {
          this.toFocusReference.clear();
        });
      }
    }
  };

  selectTo = (date, anyDate = false) => {
    const { from } = this.state;
    if ((from && dayjs(date).isAfter(dayjs(from))) || (from && dayjs(date).isSame(dayjs(from)))) {
      this.onChange(date, 'to');
      this.toFocusReference.updateValue(date);
    } else if (!from) {
      this.onChange(date, 'to');
      this.toFocusReference.updateValue(date);
      this.onChange(date, 'from');
      this.fromFocusReference.updateValue(date);
    } else if (anyDate) {
      this.onChange(date, 'to');
      this.toFocusReference.updateValue(date);

      if (dayjs(date).isBefore(dayjs(from))) {
        this.onChange(date, 'from');
        this.fromFocusReference.updateValue(date);
      }
    }
  };

  handleChange = (date) => {
    let typeError;
    if (this.props.departure !== null) {
      const endDateRange = dayjs().add(
        this.props.departure.end + this.props.departure.start - 1,
        'day'
      );
      const starDateRange = dayjs().add(this.props.departure.start, 'day');
      if (dayjs(date).isBefore(starDateRange, 'day') || dayjs(date).isAfter(endDateRange, 'day')) {
        typeError = DATE_OUT_OF_RANGE;
      }
    } else if (this.props.availableDays !== null) {
      const endDateRange = dayjs().add(this.props.availableDays, 'day');
      if (dayjs(date).isAfter(endDateRange) || dayjs(date).isSame(endDateRange, 'day')) {
        typeError = DATE_OUT_OF_RANGE;
      }
    }
    if (typeError !== undefined) {
      return;
    }
    if (!isFutureOrPresentDay(date)) {
      return;
    } else {
      const { departure } = this.props;
      if (
        departure &&
        Number(departure.end) &&
        Number(departure.end) >= 0 &&
        Number(departure.start) &&
        Number(departure.start) >= 0 &&
        !isDayInRange(date, {
          from: dayjs().add(Number(departure.start), 'day').toDate(),
          to: dayjs()
            .add(Number(departure.start) + Number(departure.end), 'day')
            .toDate()
        })
      ) {
        return;
      }
    }
    const { from, machineState } = this.state;
    if (date && !DateUtils.isPastDay(date)) {
      switch (machineState) {
        case STATES.INPSTART:
        case STATES.EMPTY:
          this.send('selectFrom', { date, anyDate: true });
          break;
        case STATES.INPEND:
          this.send('selectTo', { date, anyDate: true });
          break;
        case STATES.START:
          if ((from && dayjs(date).isBefore(dayjs(from))) || !from) {
            this.send('selectFrom', { date, anyDate: false });
          } else {
            this.send('selectTo', { date, anyDate: false });
          }
          break;
        case STATES.BOTH:
          if (from && dayjs(date).isBefore(dayjs(from))) {
            this.send('selectFrom', { date, anyDate: false });
          } else {
            this.send('selectTo', { date, anyDate: false });
          }
          break;
        default:
      }
    }
  };

  setValue = ({ start, end }) => {
    this.setState(
      {
        from: start,
        to: end,
        showCloseIcon: true
      },
      () => {
        this.toFocusReference.updateValue(end || '');
        this.fromFocusReference.updateValue(start || '');
      }
    );
  };

  reset = () => {
    this.setState({
      from: undefined,
      to: undefined,
      showCloseIcon: false
    });
    this.fromFocusReference.clear();
    this.toFocusReference.clear();
  };

  clear = (callOnClear = true) => {
    const { onClear } = this.props;
    this.setState({
      from: undefined,
      to: undefined,
      showCloseIcon: false
    });
    if (callOnClear) {
      onClear({ start: undefined, end: undefined });
    }
    this.send('clear');
    this.fromFocusReference.clear();
    this.toFocusReference.clear();
  };

  handleSelection = (selection) => {
    if (selection === 'start') {
      this.send('toStart');
    } else if (selection === 'end') {
      this.send('toEnd');
    }
  };

  focusComponent = () => {
    const { onFocus } = this.props;
    this.componentReference.className += ' fr-focus:focus';
    onFocus();
  };

  blurComponent = () => {
    if (this.state.from === undefined && this.state.to === undefined) {
      this.setState({
        typeError: undefined,
        validfrom: true,
        validto: true
      });
    }
    this.componentReference.classList.remove('fr-focus:focus');
  };

  setComponentReference = (el) => {
    this.componentReference = el;
  };

  onKeyPress = (e, name) => {
    if (e.keyCode === KEY_ENTER) {
      const { from, to } = this.state;
      const { format } = this.props;
      let typeError;
      if (
        (from === undefined && to === undefined) ||
        (to && to.toString() === 'Invalid Date' && from && from.toString() === 'Invalid Date')
      ) {
        typeError = INCORRECT_FORMAT;
        if (typeError !== undefined) {
          this.setState({
            ...this.state,
            from: undefined,
            typeError: typeError,
            validfrom: false,
            validto: false,
            to: undefined,
            flexibleDatesValues: 0
          });
          this.clear();
        } else {
          this.setState({
            flexibleDatesValues: 0,
            typeError: undefined,
            validfrom: true,
            validto: true
          });
        }
      } else if (from !== undefined && from !== '' && (to === undefined || to === '')) {
        const value = from;
        dayjs.extend(customParseFormat);
        const date1 = dayjs(value).toDate();
        const date2 = dayjs(value, format).toDate();
        let date = date1;
        if (date2.toString() !== 'Invalid Date') {
          if (
            date1.getFullYear() !== date2.getFullYear() ||
            date1.getMonth() !== date2.getMonth() ||
            date1.getDay() !== date2.getDay()
          ) {
            date = date2;
          }
        }

        const isFutureOrPresent = isFutureOrPresentDay(date);
        if (this.props.departure !== null) {
          const endDateRange = dayjs().add(
            this.props.departure.end + this.props.departure.start - 1,
            'day'
          );
          const starDateRange = dayjs().add(this.props.departure.start, 'day');
          if (
            dayjs(date).isBefore(starDateRange, 'day') ||
            dayjs(date).isAfter(endDateRange, 'day')
          ) {
            typeError = DATE_OUT_OF_RANGE;
          }
        } else if (this.props.availableDays !== null) {
          const endDateRange = dayjs().add(this.props.availableDays, 'day');
          if (dayjs(date).isAfter(endDateRange, 'day') || dayjs(date).isSame(endDateRange, 'day')) {
            typeError = DATE_OUT_OF_RANGE;
          }
        }
        if (
          !!date &&
          typeError !== DATE_OUT_OF_RANGE &&
          typeError !== INCORRECT_FORMAT &&
          isFutureOrPresent
        ) {
          this.setState({
            flexibleDatesValues: 0,
            typeError: undefined,
            validfrom: true,
            validto: true
          });
        } else {
          this.setState({
            ...this.state,
            typeError: typeError,
            from: undefined,
            validfrom: false,
            validto: false,
            to: undefined,
            flexibleDatesValues: 0
          });
          this.clear();
        }
      } else if ((from === undefined || from === '') && to !== undefined && to !== '') {
        const value = to;
        dayjs.extend(customParseFormat);
        const date1 = dayjs(value).toDate();
        const date2 = dayjs(value, format).toDate();
        let date = date1;
        if (date2.toString() !== 'Invalid Date') {
          if (
            date1.getFullYear() !== date2.getFullYear() ||
            date1.getMonth() !== date2.getMonth() ||
            date1.getDay() !== date2.getDay()
          ) {
            date = date2;
          }
        }
        const isFutureOrPresent = isFutureOrPresentDay(date);
        let typeError;
        if (this.props.departure !== null) {
          const endDateRange = dayjs().add(
            this.props.departure.end + this.props.departure.start - 1,
            'day'
          );
          const starDateRange = dayjs().add(this.props.departure.start, 'day');
          if (
            dayjs(date).isBefore(starDateRange, 'day') ||
            dayjs(date).isAfter(endDateRange, 'day')
          ) {
            typeError = DATE_OUT_OF_RANGE;
          }
        } else if (this.props.availableDays !== null) {
          const endDateRange = dayjs().add(this.props.availableDays, 'day');
          if (dayjs(date).isAfter(endDateRange) || dayjs(date).isSame(endDateRange, 'date')) {
            typeError = DATE_OUT_OF_RANGE;
          }
        }
        if (
          !!date &&
          typeError !== DATE_OUT_OF_RANGE &&
          typeError !== INCORRECT_FORMAT &&
          isFutureOrPresent
        ) {
          this.setState({
            ...this.state,
            typeError: undefined,
            from: this.state.to,
            validfrom: true,
            validto: true,
            flexibleDatesValues: 0
          });
          this.fromFocusReference.updateValue(this.state.to);
        } else {
          this.setState({
            ...this.state,
            typeError: typeError,
            from: undefined,
            validfrom: false,
            validto: false,
            to: undefined,
            flexibleDatesValues: 0
          });
          this.clear();
        }
      } else if (from !== undefined && from !== '' && to !== undefined && to !== '') {
        if (this.props.departure !== null && to && from) {
          const endDateRange = dayjs().add(
            this.props.departure.end + this.props.departure.start - 1,
            'day'
          );
          const starDateRange = dayjs().add(this.props.departure.start, 'day');
          if (dayjs(to).isBefore(starDateRange, 'day') || dayjs(to).isAfter(endDateRange, 'day')) {
            typeError = DATE_OUT_OF_RANGE;
          } else if (
            dayjs(from).isBefore(starDateRange, 'day') ||
            dayjs(from).isAfter(endDateRange, 'day')
          ) {
            typeError = DATE_OUT_OF_RANGE;
          }
        } else if (this.props.availableDays !== null && to && from) {
          const endDateRange = dayjs().add(this.props.availableDays, 'day');
          if (
            dayjs(to).isAfter(endDateRange) ||
            dayjs(from).isAfter(endDateRange) ||
            dayjs(to).isSame(endDateRange, 'date') ||
            dayjs(from).isSame(endDateRange, 'date') ||
            dayjs(from).isBefore(dayjs(new Date()), 'date')
          ) {
            typeError = DATE_OUT_OF_RANGE;
          }
        }
        if (to && from && typeError === undefined) {
          this.setState({
            ...this.state,
            typeError: undefined,
            validfrom: true,
            validto: true,
            flexibleDatesValues: 0
          });
        } else {
          this.setState({
            ...this.state,
            typeError: typeError,
            from: undefined,
            validfrom: false,
            validto: false,
            to: undefined,
            flexibleDatesValues: 0
          });
          this.clear();
        }
      }
    }
  };

  render = () => {
    const {
      format,
      showBorder,
      footer,
      datePickerContainer,
      weekDays,
      showPicker,
      departInputPlaceholderLabelKey,
      returnInputPlaceholderLabelKey,
      leftIcon,
      rightIcon: { icon: rightIcon, ...rightIconSettings },
      inputItem,
      leftInputItem,
      rightInputItem,
      showLabel,
      labelKey,
      textLabel,
      showSeparator,
      clearButton,
      onValueClass,
      errorLabel
    } = this.props;
    const { translate, direction, theme } = this.context;
    const [className, style] = getStyles(this.props, { direction, theme });
    const { from, to, showCloseIcon, validfrom, validto, numberOfMonths } = this.state;
    const buttonDisabled = (!from && !to) || (validfrom === false && validto === false);
    const rtl = direction === 'rtl';
    const classNameCmp = `${DatesRangeSelectorMobile.componentName} ${
      validfrom === false || validto === false ? 'fr-invalid' : ''
    }`;
    const minDate = from || this.currentDate;
    const leftIcons = leftIcon ? [leftIcon] : [];

    let currentOnValueClass = '';
    if (!isNilOrEmpty(onValueClass) && (typeof from !== 'undefined' || typeof to !== 'undefined')) {
      currentOnValueClass = onValueClass;
    }

    return (
      <>
        {showLabel && (
          <Text display="inline-block" textAlign="left" width="100" {...textLabel}>
            {translate(labelKey, 'Dates')}
          </Text>
        )}
        <Box
          display="flex"
          border
          className={`${className} ${classNameCmp}`}
          style={{ overflow: 'hidden', ...style }}
          dataTestId="dateRangeSelectorMobile"
          bgColor="white"
        >
          <Box
            className={`fr-input fr-input-group fr-w-100 ${currentOnValueClass}`}
            focusReferences={this.setComponentReference}
          >
            <Input
              id={`fr-depart-input${this.id}`}
              leftIcons={leftIcons}
              defaultValue={from}
              onChange={(date) => this.onChange(date, 'from')}
              label={translate(departInputPlaceholderLabelKey, 'Depart')}
              placeholder={translate(departInputPlaceholderLabelKey, 'Depart')}
              onInvalid={() => this.onInvalid('from')}
              customValidateDate={true}
              getEmptyDate={true}
              minDate={this.currentDate}
              maxDate={to}
              format={format}
              autoComplete={'off'}
              spellCheck={'off'}
              autoCorrect={'off'}
              ariaAutoComplete={'list'}
              ref={(ref) => {
                this.fromFocusReference = ref;
              }}
              onFocus={this.focusComponent}
              onBlur={this.blurComponent}
              onClick={() => {
                this.handleSelection('start');
              }}
              showFocusVisualCue={false}
              onKeyPress={(e) => {
                this.onKeyPress(e, 'from');
              }}
              notValidateDate={true}
              {...{ ...inputItem, ...leftInputItem }}
            />
            {(showSeparator || (!showSeparator && from && to)) && (
              <Box className="DayPicker-separator" />
            )}
            <Input
              id={`fr-return-input${this.id}`}
              leftIcons={leftIcons}
              defaultValue={to}
              onChange={(date) => this.onChange(date, 'to')}
              placeholder={translate(returnInputPlaceholderLabelKey, 'Return')}
              label={translate(returnInputPlaceholderLabelKey, 'Return')}
              onInvalid={() => this.onInvalid('to')}
              customValidateDate={true}
              getEmptyDate={true}
              minDate={minDate}
              format={format}
              autoComplete={'off'}
              spellCheck={'off'}
              autoCorrect={'off'}
              ariaAutoComplete={'list'}
              ref={(ref) => {
                this.toFocusReference = ref;
              }}
              onFocus={this.focusComponent}
              onBlur={this.blurComponent}
              onClick={() => this.handleSelection('end')}
              onKeyPress={(e) => {
                this.onKeyPress(e, 'from');
              }}
              showFocusVisualCue={false}
              notValidateDate={true}
              {...{ ...inputItem, ...rightInputItem }}
            />
            {showCloseIcon || (rightIcon && !showPicker) ? (
              <Box className={`fr-input-group-append ${!rtl ? 'fr-justify-content-end' : ''}`}>
                <Button
                  onClick={showCloseIcon ? this.clear : this.focusComponent}
                  color="transparent"
                  ariaLabel={translate(
                    `cmp-date-range-selector-${showCloseIcon ? 'clear-filter' : 'right-icon'}`,
                    'Clear range'
                  )}
                  paddingLeft="2"
                  dataTestId="ClearButton"
                  {...clearButton}
                >
                  <Icon
                    align="middle"
                    icon={showCloseIcon ? 'close' : rightIcon}
                    {...rightIconSettings}
                  />
                </Button>
              </Box>
            ) : null}
          </Box>
        </Box>
        {this.state.typeError !== undefined && (
          <Text
            display="inline-block"
            textAlign="left"
            width="100"
            id={`error-label-date-range-${this.id}`}
            {...errorLabel}
          >
            {this.state.typeError === INCORRECT_FORMAT
              ? translate(
                  'cmp-date-selector-incorrect-format-error-message',
                  'Incorrect format on the date set'
                )
              : translate('cmp-date-selector-date-out-range-error-message', 'Date out of range')}
          </Text>
        )}
        {showPicker && (
          <Box
            {...datePickerContainer}
            borderBottom={showBorder}
            borderLeft={showBorder}
            borderRight={showBorder}
            position="relative"
            focusReferences={this.containerReference}
            display="flex"
            flexDirection="column"
            flex="1"
            direction={direction}
          >
            <WeekDays {...weekDays} showBorder={showBorder} />
            <DayPicker
              className={`${DatesRangeSelectorMobile.componentName}-datepicker fr-date-picker-mobile fr-w-100 fr-flex-1`}
              disabledDays={{
                before:
                  this.props.departure && Number(this.props.departure.start)
                    ? dayjs().add(Number(this.props.departure.start), 'day').toDate()
                    : this.currentDate,
                after:
                  this.props.departure &&
                  Number(this.props.departure.end) >= 0 &&
                  Number(this.props.departure.start) >= 0
                    ? dayjs()
                        .add(
                          Number(this.props.departure.end) + Number(this.props.departure.start) - 1,
                          'day'
                        )
                        .toDate()
                    : dayjs()
                        .add(this.props.availableDays - 1, 'day')
                        .toDate()
              }}
              modifiers={this.modifiers}
              showWeekDays={false}
              captionElement={this.captionElement}
              initialMonth={this.currentDate}
              selectedDays={[from, { from, to }]}
              numberOfMonths={numberOfMonths}
              navbarElement={this.renderNavbarElement}
              onDayClick={this.handleChange}
              renderDay={this.renderDay}
            />
            <Footer
              {...footer}
              clear={this.clear}
              onClick={this.update}
              disabled={buttonDisabled}
              enableFlexibleDates={this.props.enableFlexibleDates}
              isCheckedFlexibleDates={this.state.isCheckedFlexibleDates}
              handleIsCheckedFlexibleDatesChange={this.handleIsCheckedFlexibleDatesChange}
              flexibleDatesRangeValues={this.props.flexibleDatesRangeValues.values}
              flexibleDatesValues={this.state.flexibleDatesValues}
              handleFlexibleDatesChange={this.handleFlexibleDatesChange}
              selectFrom={this.state.from}
              selectTo={this.state.to}
            />
          </Box>
        )}
      </>
    );
  };
}

DatesRangeSelectorMobile.propTypes = {
  /**
   * Define left icons
   */
  leftIcon: PropTypes.string,
  /**
   * Settings for caption
   */
  captionItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Default settings for both inputs
   */
  inputItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings of left input
   */
  leftInputItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings of right input
   */
  rightInputItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Setting of input
   */
  rightIcon: PropTypes.objectOf(PropTypes.any),
  /**
   * Show separator
   */
  showSeparator: PropTypes.bool,
  /**
   * Props of datePicker Container
   */
  datePickerContainer: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of week days
   */
  weekDays: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of footer buttom
   */
  footer: PropTypes.objectOf(PropTypes.any),
  /**
   * default value of date
   */
  defaultValue: PropTypes.shape({
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date)
  }),
  /**
   * day style props
   */
  dayItems: PropTypes.objectOf(PropTypes.any),
  /**
   * Modifiers style
   */
  modifiersColors: PropTypes.objectOf(PropTypes.any),
  /**
   * renderDay function
   */
  renderDay: PropTypes.func,
  /**
   * onClear ranges
   */
  onClear: PropTypes.func,
  /**
   * onChange function
   */
  onChange: PropTypes.func,
  /**
   * Patter to format date
   */
  format: PropTypes.string,
  /**
   * Patter to format date
   */
  focusReferences: PropTypes.func,
  /**
   * function fired when focus the component
   */
  onFocus: PropTypes.func,
  /**
   * Define if show or hide border
   */
  showBorder: PropTypes.bool,
  /**
   * Define if show or hide border
   */
  showPicker: PropTypes.bool,
  /**
   * Max moths to show
   */
  maxMonthsToShow: PropTypes.number,
  /**
   * Departure Date Placeholder
   */
  departInputPlaceholderLabelKey: PropTypes.string,
  /**
   * Return Date Placeholder
   */
  returnInputPlaceholderLabelKey: PropTypes.string,
  /**
   * Label key of input.
   */
  labelKey: PropTypes.string,
  /**
   * Show label of input or not
   */
  showLabel: PropTypes.bool,
  /**
   * Label Settings
   */
  textLabel: PropTypes.objectOf(PropTypes.any),
  /**
   * clear button settings
   */
  availableDays: PropTypes.number,
  departure: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string
  }),
  clearButton: PropTypes.objectOf(PropTypes.any),
  numberOfMonths: PropTypes.number,
  onValueClass: PropTypes.string,
  enableFlexibleDates: PropTypes.bool,
  checkedFlexibleDates: PropTypes.bool,
  flexibleDatesRangeValues: PropTypes.shape({
    values: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        key: PropTypes.string
      })
    ),
    defaultValue: PropTypes.number
  })
};
DatesRangeSelectorMobile.defaultProps = {
  leftIcon: 'calendar',
  departure: null,
  captionItem: {},
  inputItem: {},
  leftInputItem: {},
  rightInputItem: {},
  rightIcon: {},
  showSeparator: true,
  datePickerContainer: {},
  weekDays: {},
  clearButton: {},
  footer: {
    textTransform: 'uppercase'
  },
  defaultValue: {
    start: undefined,
    end: undefined
  },
  dayItems: {
    fontSize: '4',
    fontWeight: 'bold'
  },
  availableDays: null,
  modifiersColors: {
    beginRange: { bgColor: 'primary', textColor: 'white', padding: '2', roundedLeft: '1' },
    endRange: { bgColor: 'primary', textColor: 'white', padding: '2', roundedRight: '1' },
    selected: { bgColor: 'primary-3', textColor: 'dark', padding: '2' },
    today: {},
    past: {}
  },
  renderDay: null,
  onClear: () => {},
  onChange: () => {},
  format: 'MM/DD/YYYY',
  focusReferences: () => {},
  onFocus: () => {},
  showBorder: false,
  showPicker: true,
  maxMonthsToShow: null,
  departInputPlaceholderLabelKey: 'cmp-label--datepicker-depart',
  returnInputPlaceholderLabelKey: 'cmp-label--datepicker-return',
  labelKey: 'cmp-date-range-selector-label',
  showLabel: false,
  textLabel: {},
  numberOfMonths: 3,
  onValueClass: 'fr-value-selected',
  enableFlexibleDates: false,
  checkedFlexibleDates: false,
  flexibleDatesRangeValues: {
    values: [
      { name: 'cmp-flexible-dates-opt1', value: 1, key: 'value1' },
      { name: 'cmp-flexible-dates-opt2', value: 2, key: 'value2' },
      { name: 'cmp-flexible-dates-opt3', value: 3, key: 'value3' },
      { name: 'cmp-flexible-dates-opt4', value: 4, key: 'value4' },
      { name: 'cmp-flexible-dates-opt5', value: 5, key: 'value5' }
    ],
    defaultValue: 4
  }
};

DatesRangeSelectorMobile.contextType = Context;

DatesRangeSelectorMobile.componentName = 'DatesRangeSelectorMobile';

export default DatesRangeSelectorMobile;
